// ═══════════════════════════════════════════════════════════════════
// DRC SUBMISSION v2 — Complete Design Review Committee Submission Workflow
// Document Hierarchy: Declaration (1994) > Design Guidelines (2008)
// ═══════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import {
  Upload, Check, X, ChevronDown, ChevronRight, FileText, Link2,
  AlertTriangle, CheckCircle, Clock, Send, Save, ArrowLeft,
  Shield, Scale, BookOpen, Loader2
} from "lucide-react";

// ═══════════════════════════════════════════════════════════════════
// DATA CONSTANTS (from Declaration & Design Guidelines)
// ═══════════════════════════════════════════════════════════════════

const AUTHORITY = {
  DECLARATION: { level: 1, label: "Recorded Declaration", badge: "DECLARATION", color: "text-red-800", bg: "bg-red-100", border: "border-red-200" },
  GUIDELINE: { level: 2, label: "Design Guidelines", badge: "GUIDELINE", color: "text-amber-800", bg: "bg-amber-100", border: "border-amber-200" },
  CONFLICT: { level: 0, label: "CONFLICT — Declaration Controls", badge: "CONFLICT", color: "text-red-600", bg: "bg-red-50", border: "border-red-300" },
};

const SUBMISSION_SECTIONS = [
  {
    id: "owner_info", title: "Owner & Project Information", icon: "1", type: "form",
    description: "Applicant identification and project overview",
    section: "Declaration \u00A77.04", required: true,
    fields: [
      { id: "oi_name", label: "Owner / Applicant Legal Name", type: "text", required: true },
      { id: "oi_tract", label: "Tract Number", type: "text", required: true },
      { id: "oi_section", label: "Section (Legal Description)", type: "text", required: true },
      { id: "oi_address", label: "Mailing Address", type: "text", required: true },
      { id: "oi_email", label: "Email Address", type: "text", required: true },
      { id: "oi_phone", label: "Phone Number", type: "text", required: true },
      { id: "oi_architect", label: "Licensed Architect / Building Designer Name", type: "text", required: true },
      { id: "oi_architect_license", label: "Architect License Number", type: "text", required: true },
      { id: "oi_contractor", label: "General Contractor Name", type: "text", required: false },
      { id: "oi_project_desc", label: "Description of Proposed Improvements", type: "textarea", required: true },
      { id: "oi_timeline", label: "Estimated Construction Timeline", type: "text", required: true },
    ],
  },
  {
    id: "site_plan", title: "Site Plan Package", icon: "2", type: "upload",
    description: "Must show all site-specific conditions. Two complete copies at 1 inch = 100 feet scale.",
    section: "Declaration \u00A77.04(a) / Design Guidelines", required: true,
    checklist: [
      { id: "sp_envelope", label: "Building Envelope location clearly marked", required: true, source: "Design Guidelines" },
      { id: "sp_undisturbed", label: "Undisturbed Tract Area clearly delineated", required: true, source: "Design Guidelines" },
      { id: "sp_structures", label: "All proposed structures (residence, garage, caretaker unit, outbuildings)", required: true, source: "Declaration \u00A77.04(a)" },
      { id: "sp_elevation", label: "Finished elevation of Residence, outbuildings, or other improvements", required: true, source: "Declaration \u00A77.04(a)(i)" },
      { id: "sp_footprint", label: "Building footprint with dimensions from front, rear, and side property lines", required: true, source: "Declaration \u00A77.04(a)(ii)" },
      { id: "sp_driveway", label: "Driveway alignment (designed to minimize disturbance; single access point only)", required: true, source: "Design Guidelines" },
      { id: "sp_parking", label: "Parking areas and garage placement", required: true, source: "Declaration \u00A77.19" },
      { id: "sp_walkways", label: "Driveways and walkways located on the Tract", required: true, source: "Declaration \u00A77.04(a)(iii)" },
      { id: "sp_setbacks", label: "Setbacks shown (100 ft minimum from all property lines)", required: true, source: "Declaration \u00A77.18" },
      { id: "sp_ridgeline", label: "Ridgeline protection demonstration", required: true, source: "Design Guidelines" },
      { id: "sp_natural", label: "Natural features mapped (trees, rock outcrops, drainage, slopes)", required: true, source: "Design Guidelines" },
      { id: "sp_existing", label: "Any existing structures on the Tract", required: true, source: "Declaration \u00A77.04(a)(iv)" },
      { id: "sp_utilities_loc", label: "Location of improvements with respect to utility lines", required: true, source: "Declaration \u00A77.04(a)(v)" },
    ],
  },
  {
    id: "grading_drainage", title: "Grading & Drainage Plan", icon: "3", type: "upload",
    description: "Site grading should be limited. Drainage plans should utilize natural drainage courses.",
    section: "Design Guidelines", required: true,
    checklist: [
      { id: "gd_cutfill", label: "Cut/fill diagrams", required: true, source: "Design Guidelines" },
      { id: "gd_driveway_grade", label: "Driveway grading (\u226410% slope)", required: true, source: "Design Guidelines" },
      { id: "gd_drainage", label: "Drainage paths and erosion control plan", required: true, source: "Design Guidelines" },
      { id: "gd_retaining", label: "Retaining wall locations (\u22644 ft height unless terraced)", required: true, source: "Design Guidelines" },
      { id: "gd_erosion", label: "Erosion control measures during and after construction", required: true, source: "Design Guidelines" },
    ],
  },
  {
    id: "architectural_plans", title: "Architectural Plans", icon: "4", type: "upload",
    description: "All buildings shall be designed by a licensed architect or licensed building designer. Two complete sets required.",
    section: "Declaration \u00A77.04(b) / Design Guidelines", required: true,
    checklist: [
      { id: "ap_floorplans", label: "Floor plans of all levels with sufficient detail", required: true, source: "Declaration \u00A77.04(b)(i)" },
      { id: "ap_sqft", label: "Total square footage for each level (min 2,000 sq ft single-story; min 2,500 sq ft multi-level; basements excluded)", required: true, source: "Declaration \u00A77.15 \u2014 CONTROLS" },
      { id: "ap_elevations", label: "Building elevations on all sides (roof form, windows, siding, doors)", required: true, source: "Declaration \u00A77.04(b)(iii)" },
      { id: "ap_story_compliance", label: "Story count documented \u2014 Declaration permits single-story AND multi-level", required: true, source: "Declaration \u00A77.15 \u2014 CONTROLS" },
      { id: "ap_roof", label: "Roof plan showing height relative to natural grade", required: true, source: "Design Guidelines" },
      { id: "ap_height", label: "Building height documentation (Eagle County + ACC limits)", required: true, source: "Design Guidelines" },
      { id: "ap_windows", label: "Window and door schedule", required: true, source: "Design Guidelines" },
      { id: "ap_licensed", label: "Plans prepared by licensed architect or building designer", required: true, source: "Design Guidelines" },
    ],
  },
  {
    id: "materials_colors", title: "Materials & Colors Schedule", icon: "5", type: "upload",
    description: "Acceptable materials: log, timber, stone, stucco, wood siding. Earth tones required; bright colors prohibited.",
    section: "Declaration \u00A77.32 / Design Guidelines", required: true,
    checklist: [
      { id: "mc_materials_desc", label: "Written description of materials for roof and exterior walls", required: true, source: "Declaration \u00A77.04(b)(iv)" },
      { id: "mc_color_spec", label: "Color of any paint or stain to be applied to exteriors", required: true, source: "Declaration \u00A77.04(b)(vi)" },
      { id: "mc_earth_tones", label: "Earth tone colors confirmed (bright colors prohibited)", required: true, source: "Declaration \u00A77.32" },
      { id: "mc_samples", label: "Material samples or manufacturer specifications provided", required: true, source: "Design Guidelines" },
      { id: "mc_no_metal", label: "Confirmed no all-metal or predominantly metal structures", required: true, source: "Declaration \u00A77.17" },
    ],
  },
  {
    id: "lighting_plan", title: "Exterior Lighting Plan", icon: "6", type: "upload",
    description: "Downlighting only. No uplighting. No colored lights except holiday. Security lighting must be motion-activated.",
    section: "Declaration \u00A77.34 / Design Guidelines", required: true,
    checklist: [
      { id: "lt_downlight", label: "All fixtures are downlighting only (no uplighting)", required: true, source: "Design Guidelines" },
      { id: "lt_no_continuous", label: "No continuous nighttime illumination (sunset to sunrise)", required: true, source: "Declaration \u00A77.34" },
      { id: "lt_no_colored", label: "No colored lights (except seasonal/holiday)", required: true, source: "Design Guidelines" },
      { id: "lt_security", label: "Security lighting is motion-activated only", required: true, source: "Design Guidelines" },
      { id: "lt_gas_limit", label: "Gas lights \u2264200 watts in front of Residence only", required: true, source: "Declaration \u00A77.34" },
      { id: "lt_fixture_specs", label: "Fixture specifications and placement locations shown", required: true, source: "Design Guidelines" },
    ],
  },
  {
    id: "landscaping_plan", title: "Landscaping & Revegetation Plan", icon: "7", type: "upload",
    description: "Landscaping plan required within 60 days of CO. Installation within 365 days.",
    section: "Declaration \u00A77.09 / Design Guidelines", required: true,
    checklist: [
      { id: "lp_planting", label: "Planting plan showing location of all trees, shrubs, grass", required: true, source: "Declaration \u00A77.09" },
      { id: "lp_reveg", label: "Revegetation plan for disturbed areas", required: true, source: "Design Guidelines" },
      { id: "lp_tree_removal", label: "Tree removal request (no live trees removed outside envelope without approval)", required: true, source: "Declaration \u00A77.09" },
      { id: "lp_irrigation", label: "Irrigation plan (max 25,000 sq ft under irrigation; naturalized)", required: true, source: "Declaration \u00A77.38" },
      { id: "lp_outside_envelope", label: "Landscaping outside building envelope separately identified", required: false, source: "Design Guidelines" },
    ],
  },
  {
    id: "utilities_plan", title: "Utilities Plan", icon: "8", type: "upload",
    description: "All utilities must be underground unless otherwise approved by the ACC.",
    section: "Declaration \u00A77.14 / Design Guidelines", required: true,
    checklist: [
      { id: "ut_underground", label: "All utilities confirmed underground", required: true, source: "Declaration \u00A77.14" },
      { id: "ut_water", label: "Water supply connection / well location", required: true, source: "Design Guidelines" },
      { id: "ut_septic", label: "Septic / wastewater system location and design", required: true, source: "Design Guidelines" },
      { id: "ut_electric", label: "Electrical service routing (underground)", required: true, source: "Declaration \u00A77.14" },
      { id: "ut_propane", label: "Propane tank location (screened from view)", required: true, source: "Declaration \u00A77.29" },
      { id: "ut_telecom", label: "Telecom / data conduit routing", required: true, source: "Design Guidelines" },
    ],
  },
  {
    id: "construction_mgmt", title: "Construction Management Plan", icon: "9", type: "upload",
    description: "Required construction procedures including site protection, access, staging, and timeline.",
    section: "Design Guidelines", required: true,
    checklist: [
      { id: "cm_site_protection", label: "Site protection plan (erosion barriers, tree protection, silt fencing)", required: true, source: "Design Guidelines" },
      { id: "cm_access", label: "Construction access route identified", required: true, source: "Design Guidelines" },
      { id: "cm_staging", label: "Staging area / material storage area designated", required: true, source: "Design Guidelines" },
      { id: "cm_temp_structures", label: "Temporary structures and sanitary facilities plan", required: true, source: "Design Guidelines" },
      { id: "cm_trash", label: "Trash and debris removal plan", required: true, source: "Declaration \u00A77.31 / Design Guidelines" },
      { id: "cm_timeline", label: "Construction timeline (commence within 3 months; complete within 9 months)", required: true, source: "Declaration \u00A77.08" },
      { id: "cm_hours", label: "Construction hours and noise mitigation", required: true, source: "Declaration \u00A77.36" },
    ],
  },
  {
    id: "fence_gate", title: "Fence & Gate Plan", icon: "F", type: "upload",
    description: "No fences without approval. Must be ranch-style or natural. No fences adjacent to Roadway Easement.",
    section: "Declaration \u00A77.20 / Design Guidelines", required: false,
    conditional: "If fencing or gates are proposed",
    checklist: [
      { id: "fg_type", label: "Fence type specified (ranch-style or natural materials)", required: true, source: "Design Guidelines" },
      { id: "fg_materials", label: "Materials specification provided", required: true, source: "Declaration \u00A77.20" },
      { id: "fg_location", label: "Location shown (\u226510 ft from tract boundaries)", required: true, source: "Declaration \u00A77.20 / \u00A72.01(b)(i)" },
      { id: "fg_no_roadway", label: "Confirmed no fencing adjacent to Roadway Easement", required: true, source: "Declaration \u00A77.20" },
      { id: "fg_cattleguard", label: "Cattleguard details (if fence crosses Road)", required: false, source: "Declaration \u00A72.04" },
      { id: "fg_design_criteria", label: "Compliance with Association design criteria for fences", required: true, source: "Declaration \u00A77.20" },
    ],
  },
  {
    id: "outbuilding_caretaker", title: "Outbuilding & Caretaker Residence Plans", icon: "O", type: "upload",
    description: "Max 2 outbuildings, combined max 3,200 sq ft, max 18 ft height. Caretaker residence 600\u20132,000 sq ft.",
    section: "Declaration \u00A77.11, \u00A77.16, \u00A77.17", required: false,
    conditional: "If outbuildings or caretaker residence are proposed",
    checklist: [
      { id: "ob_count", label: "Number of outbuildings (max 2)", required: true, source: "Declaration \u00A77.17" },
      { id: "ob_sqft", label: "Combined square footage shown (max 3,200 sq ft)", required: true, source: "Declaration \u00A77.17" },
      { id: "ob_height", label: "Height documentation (max 18 ft)", required: true, source: "Declaration \u00A77.17" },
      { id: "ob_no_metal", label: "No all-metal or predominantly metal structures confirmed", required: true, source: "Declaration \u00A77.17" },
      { id: "ob_no_modular", label: "No modular construction confirmed", required: true, source: "Declaration \u00A77.13" },
      { id: "ob_appearance", label: "Appearance compatible with main Residence", required: true, source: "Declaration \u00A77.17 / \u00A77.26(g)" },
      { id: "ct_sqft", label: "Caretaker residence 600\u20132,000 sq ft (if applicable)", required: false, source: "Declaration \u00A77.16" },
      { id: "ct_no_separate_meter", label: "No separately metered electrical; shared well water", required: false, source: "Declaration \u00A77.16" },
    ],
  },
  {
    id: "satellite_antenna", title: "Satellite Dish / Antenna Plan", icon: "S", type: "upload",
    description: "No satellite dish >24 inches without ACC approval and screening plan.",
    section: "Declaration \u00A77.22", required: false,
    conditional: "If satellite dish exceeds 24 inches in diameter",
    checklist: [
      { id: "sa_size", label: "Dish/antenna size and specifications", required: true, source: "Declaration \u00A77.22" },
      { id: "sa_location", label: "Proposed location on Tract", required: true, source: "Declaration \u00A77.22" },
      { id: "sa_screening", label: "Screening plan (landscaping or other appropriate forms)", required: true, source: "Declaration \u00A77.22" },
    ],
  },
  {
    id: "storage_tanks", title: "Storage Tank Screening Plan", icon: "T", type: "upload",
    description: "No elevated tanks unless screened from view from other Tracts and Roads.",
    section: "Declaration \u00A77.29", required: false,
    conditional: "If elevated storage tanks are proposed",
    checklist: [
      { id: "st_type", label: "Tank type and purpose", required: true, source: "Declaration \u00A77.29" },
      { id: "st_location", label: "Location shown on site plan", required: true, source: "Declaration \u00A77.29" },
      { id: "st_screening", label: "Screening plan approved by ACC", required: true, source: "Declaration \u00A77.29" },
    ],
  },
  {
    id: "animal_facilities", title: "Animal Facilities Plan", icon: "A", type: "upload",
    description: "Max 5 animal units. No donkeys, burros, jackasses, or full-size pigs.",
    section: "Declaration \u00A77.26 / Design Guidelines", required: false,
    conditional: "If animal housing, corrals, or pet runs are proposed",
    checklist: [
      { id: "af_type", label: "Type and number of animals (max 5 animal units)", required: true, source: "Declaration \u00A77.26(a)" },
      { id: "af_corral", label: "Corral/outbuilding plans (similar appearance to Residence)", required: true, source: "Declaration \u00A77.26(g)" },
      { id: "af_fencing", label: "Animal confinement fencing (ACC-approved in writing)", required: true, source: "Declaration \u00A77.26(h)" },
      { id: "af_sanitary", label: "Sanitary conditions plan (no open manure piles)", required: true, source: "Declaration \u00A77.26(d)" },
      { id: "af_pet_run", label: "Pet run details if applicable", required: false, source: "Declaration \u00A77.26(j)" },
    ],
  },
  {
    id: "fees_deposits", title: "Fees & Deposits", icon: "$", type: "payment",
    description: "Design Review Fee and Construction Deposit required before review begins.",
    section: "Declaration \u00A77.08 / Design Guidelines", required: true,
    checklist: [
      { id: "fd_review_fee", label: "Design Review Fee submitted", required: true, source: "Design Guidelines" },
      { id: "fd_construction_deposit", label: "Construction Deposit submitted (up to $250.00, refundable)", required: true, source: "Declaration \u00A77.08" },
      { id: "fd_consultant", label: "Consultant fee acknowledgment (if ACC hires architect/engineer for review)", required: true, source: "Design Guidelines" },
    ],
  },
  {
    id: "county_approvals", title: "Eagle County Approvals", icon: "C", type: "upload",
    description: "Eagle County may impose further restrictions.",
    section: "Design Guidelines", required: false,
    conditional: "If Eagle County approvals are required before ACC final approval",
    checklist: [
      { id: "ca_building_permit", label: "Eagle County building permit application or approval", required: true, source: "Design Guidelines" },
      { id: "ca_septic", label: "Eagle County septic/wastewater approval", required: true, source: "Design Guidelines" },
      { id: "ca_access", label: "Eagle County road access permit (if applicable)", required: false, source: "Design Guidelines" },
    ],
  },
  {
    id: "geotech_reports", title: "Geotechnical & Environmental Reports", icon: "G", type: "upload",
    description: "Required if slopes, retaining walls, drainage issues, or wildfire risk exists.",
    section: "Design Guidelines", required: false,
    conditional: "If site has slopes, retaining walls, drainage issues, or wildfire risk",
    checklist: [
      { id: "gr_geotech", label: "Geotechnical report (slopes, soil stability, foundation recommendations)", required: true, source: "Design Guidelines" },
      { id: "gr_wildfire", label: "Wildfire mitigation plan (defensible space, materials)", required: false, source: "Design Guidelines" },
      { id: "gr_environmental", label: "Environmental impact assessment (if required by County)", required: false, source: "Design Guidelines" },
    ],
  },
  {
    id: "owner_certifications", title: "Owner Certifications", icon: "!", type: "certification",
    description: "Owner must certify compliance before submission can be accepted.",
    section: "Declaration \u00A77.02", required: true,
    checklist: [
      { id: "oc_declaration", label: "I have reviewed the Restated and Amended Declaration (recorded Eagle County, Book 661, Page 273) and understand it is the supreme governing document", required: true, source: "Declaration" },
      { id: "oc_guidelines", label: "I have reviewed the 2008 Design Guidelines and understand they are supplementary to, and cannot supersede, the recorded Declaration", required: true, source: "Declaration \u00A74.03" },
      { id: "oc_hierarchy", label: "I understand that where the Design Guidelines conflict with the Declaration, the Declaration controls. The ACC cannot impose restrictions beyond those in the recorded Declaration without a formal amendment approved by 67% of Tract Owners.", required: true, source: "Declaration \u00A712.04" },
      { id: "oc_envelope", label: "All proposed improvements are within the approved building envelope or have been separately identified for ACC review", required: true, source: "Design Guidelines" },
      { id: "oc_no_work", label: "No work (including staking, clearing, excavation, grading) will begin until written ACC approval is issued per Declaration \u00A77.02", required: true, source: "Declaration \u00A77.02" },
      { id: "oc_changes", label: "Any changes during construction will require resubmission and prior written ACC approval per Declaration \u00A77.02", required: true, source: "Declaration \u00A77.02" },
      { id: "oc_licensed", label: "All buildings have been designed by a licensed architect or licensed building designer", required: true, source: "Design Guidelines" },
      { id: "oc_accurate", label: "All information provided in this submission is true and accurate to the best of my knowledge", required: true, source: "General" },
    ],
  },
];

const COMPLIANCE_RULES = [
  { id: "cr_setback", label: "Minimum 100 ft setback from all property lines", section: "Declaration \u00A77.18", category: "Siting", authority: "DECLARATION" },
  { id: "cr_envelope", label: "Construction within building envelope (if established by ACC)", section: "Design Guidelines", category: "Siting", authority: "GUIDELINE", note: "Envelope concept from Guidelines; Declaration \u00A77.18 setbacks are binding minimum" },
  { id: "cr_ridgeline", label: "Ridgeline sensitivity considered in siting", section: "Design Guidelines", category: "Siting", authority: "GUIDELINE" },
  { id: "cr_min_size_single", label: "Minimum 2,000 sq ft for single-story Residence (ground floor, excl. basements)", section: "Declaration \u00A77.15", category: "Structure", authority: "DECLARATION" },
  { id: "cr_min_size_multi", label: "Minimum 2,500 sq ft for multi-level Residence (excl. basements)", section: "Declaration \u00A77.15", category: "Structure", authority: "DECLARATION" },
  { id: "cr_height_limitation", label: "Residence height limitation (established by ACC on case-by-case basis)", section: "Declaration \u00A77.15", category: "Structure", authority: "DECLARATION" },
  { id: "cr_single_story_conflict", label: "CONFLICT: Design Guidelines restrict to 'single-story' \u2014 BUT Declaration \u00A77.15 explicitly provides for multi-level Residences. DECLARATION CONTROLS.", section: "CONFLICT \u2014 Declaration \u00A77.15 controls", category: "Structure", authority: "CONFLICT" },
  { id: "cr_garage", label: "Garage with space for at least 2 vehicles", section: "Declaration \u00A77.19", category: "Structure", authority: "DECLARATION" },
  { id: "cr_no_temp_structures", label: "No temporary structures for residential use (trailers, mobile homes, tents)", section: "Declaration \u00A77.12", category: "Structure", authority: "DECLARATION" },
  { id: "cr_use_restrictions", label: "Single-Family Residence only; plus one caretaker residence and two outbuildings max", section: "Declaration \u00A77.11", category: "Structure", authority: "DECLARATION" },
  { id: "cr_outbuilding_count", label: "Maximum 2 outbuildings per Tract", section: "Declaration \u00A77.17", category: "Outbuildings", authority: "DECLARATION" },
  { id: "cr_outbuilding_size", label: "Combined outbuilding area max 3,200 sq ft", section: "Declaration \u00A77.17", category: "Outbuildings", authority: "DECLARATION" },
  { id: "cr_outbuilding_height", label: "Outbuilding height max 18 ft above grade", section: "Declaration \u00A77.17", category: "Outbuildings", authority: "DECLARATION" },
  { id: "cr_caretaker_size", label: "Caretaker Residence: min 600 sq ft, max 2,000 sq ft", section: "Declaration \u00A77.16", category: "Outbuildings", authority: "DECLARATION" },
  { id: "cr_no_modular", label: "No modular construction; all structures assembled on-site", section: "Declaration \u00A77.13", category: "Materials", authority: "DECLARATION" },
  { id: "cr_no_metal", label: "No all-metal or predominantly metal outbuildings", section: "Declaration \u00A77.17", category: "Materials", authority: "DECLARATION" },
  { id: "cr_earth_tones", label: "Earth tone colors required; bright colors prohibited", section: "Declaration \u00A77.32", category: "Materials", authority: "DECLARATION" },
  { id: "cr_approved_materials", label: "Materials should blend with natural landscape", section: "Design Guidelines", category: "Materials", authority: "GUIDELINE" },
  { id: "cr_utilities_underground", label: "All utilities underground unless ACC approves aboveground", section: "Declaration \u00A77.14", category: "Utilities", authority: "DECLARATION" },
  { id: "cr_no_continuous_light", label: "No continuous nighttime illumination (sunset to sunrise)", section: "Declaration \u00A77.34", category: "Lighting", authority: "DECLARATION" },
  { id: "cr_gas_lights", label: "Gas lights \u2264200 watts in front of Residence or for security", section: "Declaration \u00A77.34", category: "Lighting", authority: "DECLARATION" },
  { id: "cr_downlighting", label: "Downlighting preferred; no uplighting", section: "Design Guidelines", category: "Lighting", authority: "GUIDELINE" },
  { id: "cr_irrigation_limit", label: "Max 25,000 sq ft under irrigation per Tract", section: "Declaration \u00A77.38", category: "Landscaping", authority: "DECLARATION" },
  { id: "cr_no_tree_removal", label: "No live trees cut or removed without ACC approval", section: "Declaration \u00A77.09", category: "Landscaping", authority: "DECLARATION" },
  { id: "cr_landscape_deadline", label: "Landscaping installed within 365 days of CO", section: "Declaration \u00A77.09", category: "Landscaping", authority: "DECLARATION" },
  { id: "cr_driveway_grade", label: "Driveway grade \u226410% recommended", section: "Design Guidelines", category: "Grading", authority: "GUIDELINE" },
  { id: "cr_retaining_wall", label: "Retaining walls \u22644 ft unless terraced", section: "Design Guidelines", category: "Grading", authority: "GUIDELINE" },
  { id: "cr_fence_no_roadway", label: "No fence adjacent to Roadway Easement without ACC approval", section: "Declaration \u00A77.20", category: "Fencing", authority: "DECLARATION" },
  { id: "cr_fence_design", label: "All fences must comply with Association design criteria", section: "Declaration \u00A77.20", category: "Fencing", authority: "DECLARATION" },
  { id: "cr_fireplace_screens", label: "All fireplaces/chimneys/barbecues have spark-arresting screens", section: "Declaration \u00A77.39", category: "Safety", authority: "DECLARATION" },
  { id: "cr_no_fireworks", label: "No fireworks on any Tract", section: "Declaration \u00A77.39", category: "Safety", authority: "DECLARATION" },
  { id: "cr_animal_limit", label: "Max 5 animal units on a Tract", section: "Declaration \u00A77.26(a)", category: "Animals", authority: "DECLARATION" },
  { id: "cr_no_prohibited_animals", label: "No donkeys, burros, jackasses, or full-size pigs", section: "Declaration \u00A77.26(b)", category: "Animals", authority: "DECLARATION" },
  { id: "cr_no_resubdivision", label: "No resubdivision of Tract under any circumstances", section: "Declaration \u00A77.33", category: "Lot", authority: "DECLARATION" },
  { id: "cr_commence_3months", label: "Construction must commence within 3 months of approval", section: "Declaration \u00A77.08", category: "Timeline", authority: "DECLARATION" },
  { id: "cr_complete_9months", label: "Exterior complete and CO issued within 9 months", section: "Declaration \u00A77.08", category: "Timeline", authority: "DECLARATION" },
  { id: "cr_deposit_forfeit", label: "Deposit forfeited if CO not issued within 9 months", section: "Declaration \u00A77.08", category: "Timeline", authority: "DECLARATION" },
];

const TIMELINE_EVENTS = [
  { id: "t_submit", label: "Submission Received", days: 0, desc: "All required documents received by ACC" },
  { id: "t_preliminary", label: "Preliminary Review", days: 7, desc: "ACC staff verifies completeness" },
  { id: "t_review", label: "ACC Review Deadline", days: 30, desc: "Committee must act (Declaration \u00A77.06)" },
  { id: "t_resubmit", label: "Certified Mail Resubmission", days: 31, desc: "If no response, resubmit via certified mail" },
  { id: "t_deemed", label: "Deemed Approved", days: 61, desc: "If still no response \u2014 approved as submitted" },
  { id: "t_construction", label: "Construction Must Begin", days: 90, desc: "Within 3 months of approval (\u00A77.08)" },
  { id: "t_complete", label: "Construction & CO Deadline", days: 360, desc: "9 months from commencement" },
  { id: "t_landscape_plan", label: "Landscaping Plan Due", days: 420, desc: "Within 60 days of CO" },
  { id: "t_landscape_install", label: "Landscaping Installation", days: 725, desc: "Within 365 days of CO" },
];

const REVIEW_STAGES = [
  { id: "preliminary", label: "Preliminary", desc: "Completeness check" },
  { id: "full_review", label: "Full Review", desc: "Design & compliance" },
  { id: "conditions", label: "Conditions", desc: "Imposed conditions" },
  { id: "final", label: "Final Approval", desc: "Written approval" },
  { id: "construction", label: "Construction", desc: "Active building" },
  { id: "final_inspection", label: "Inspection", desc: "Deposit refund" },
];

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function DRCSubmissionPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("submissions");
  const [expandedSections, setExpandedSections] = useState({ owner_info: true });
  const [checkedItems, setCheckedItems] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [fileLinks, setFileLinks] = useState({});
  const [complianceChecks, setComplianceChecks] = useState({});
  const [enabledOptional, setEnabledOptional] = useState({});
  const [formData, setFormData] = useState({});
  const [auditLog, setAuditLog] = useState([]);
  const [linkingTarget, setLinkingTarget] = useState(null);
  const [submissionState, setSubmissionState] = useState("draft");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [filterAudit, setFilterAudit] = useState("all");
  const [reviewStage, setReviewStage] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef({});

  const addAudit = useCallback((type, action, detail, section = "") => {
    setAuditLog(prev => [{
      id: Date.now() + Math.random(),
      type, action, detail, section,
      timestamp: new Date().toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }),
      user: formData.oi_name || user?.name || "System",
    }, ...prev]);
  }, [formData.oi_name, user?.name]);

  const getSectionStatus = (section) => {
    if (!section.required && !enabledOptional[section.id]) return "na";
    if (section.type === "form") {
      const reqFields = section.fields.filter(f => f.required);
      const filled = reqFields.filter(f => formData[f.id]?.trim());
      if (filled.length === reqFields.length) return "complete";
      if (filled.length > 0) return "partial";
      return "missing";
    }
    if (section.type === "certification") {
      const reqItems = section.checklist.filter(c => c.required);
      const checked = reqItems.filter(c => checkedItems[c.id]);
      if (checked.length === reqItems.length) return "complete";
      if (checked.length > 0) return "partial";
      return "missing";
    }
    const reqItems = section.checklist.filter(c => c.required);
    const checked = reqItems.filter(c => checkedItems[c.id]);
    const files = uploadedFiles[section.id] || [];
    if (checked.length === reqItems.length && files.length > 0) return "complete";
    if (checked.length > 0 || files.length > 0) return "partial";
    return "missing";
  };

  const activeSections = SUBMISSION_SECTIONS.filter(s => s.required || enabledOptional[s.id]);
  const totalSections = activeSections.length;
  const completeSections = activeSections.filter(s => getSectionStatus(s) === "complete").length;
  const progress = totalSections > 0 ? Math.round((completeSections / totalSections) * 100) : 0;
  const complianceCompleted = COMPLIANCE_RULES.filter(r => complianceChecks[r.id]).length;
  const isReady = completeSections === totalSections && complianceCompleted === COMPLIANCE_RULES.length;

  useEffect(() => {
    if (isReady && submissionState !== "submitted") setSubmissionState("ready");
    else if (completeSections > 0 && submissionState !== "submitted") setSubmissionState("incomplete");
  }, [isReady, completeSections, submissionState]);

  const complianceCategories = [...new Set(COMPLIANCE_RULES.map(r => r.category))];

  const handleCheck = (id, label, sectionTitle) => {
    setCheckedItems(prev => ({ ...prev, [id]: !prev[id] }));
    addAudit("verify", !checkedItems[id] ? "Verified" : "Unverified", label, sectionTitle);
  };

  const handleFileUpload = (sectionId, files) => {
    const newFiles = Array.from(files).map(f => ({
      id: crypto.randomUUID(), name: f.name, size: `${(f.size / 1024).toFixed(1)} KB`,
      uploadedAt: new Date().toLocaleString(), file: f,
    }));
    setUploadedFiles(prev => ({ ...prev, [sectionId]: [...(prev[sectionId] || []), ...newFiles] }));
    newFiles.forEach(f => addAudit("upload", "Document Uploaded", f.name, sectionId));
  };

  const handleRemoveFile = (sectionId, fileId) => {
    const file = (uploadedFiles[sectionId] || []).find(f => f.id === fileId);
    setUploadedFiles(prev => ({ ...prev, [sectionId]: (prev[sectionId] || []).filter(f => f.id !== fileId) }));
    if (file) addAudit("remove", "Document Removed", file.name, sectionId);
  };

  const handleLink = (itemId, sectionId, fileId) => {
    const file = (uploadedFiles[sectionId] || []).find(f => f.id === fileId);
    if (file) {
      setFileLinks(prev => ({ ...prev, [itemId]: { sectionId, fileId, fileName: file.name } }));
      addAudit("link", "Linked", `${file.name}`, sectionId);
    }
    setLinkingTarget(null);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Save submission to backend
      const payload = {
        property_id: formData.oi_tract || "TBD",
        project_type: "New Construction",
        project_description: formData.oi_project_desc || "DRC Full Submission",
        form_data: formData,
        checked_items: checkedItems,
        compliance_checks: complianceChecks,
        enabled_optional: enabledOptional,
        file_links: fileLinks,
        audit_log: auditLog,
        submission_state: "submitted",
      };
      
      const res = await axios.post(`${API}/drc`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Upload all files for each section
      for (const [sectionId, files] of Object.entries(uploadedFiles)) {
        if (files.length > 0 && files[0].file) {
          const fd = new FormData();
          files.forEach(f => { if (f.file) fd.append("files", f.file); });
          const cats = files.map(() => sectionId).join(",");
          fd.append("categories", cats);
          try {
            await axios.post(`${API}/file-uploads/drc/${res.data.submission_id}`, fd, {
              headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
            });
          } catch (e) { /* continue with other sections */ }
        }
      }

      addAudit("submit", "Package Submitted", `Submission ID: ${res.data.submission_id}`);
      setSubmissionState("submitted");
      setShowConfirmModal(false);
      toast.success("DRC Submission Package sent to ACC for review");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = () => {
    addAudit("status", "Draft Saved", "Current progress saved");
    toast.success("Draft saved");
  };

  const filteredAudit = filterAudit === "all" ? auditLog : auditLog.filter(e => e.type === filterAudit);

  const statusBadge = (status) => {
    if (status === "complete") return <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px] h-5">Complete</Badge>;
    if (status === "partial") return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] h-5">In Progress</Badge>;
    if (status === "missing") return <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] h-5">Missing</Badge>;
    return <Badge className="bg-gray-100 text-gray-500 border-gray-200 text-[10px] h-5">N/A</Badge>;
  };

  const authorityBadge = (source) => {
    if (!source) return null;
    if (source.includes("CONTROLS")) return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">DECLARATION CONTROLS</span>;
    if (source.includes("Declaration") && !source.includes("Design Guidelines")) return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-800">DECLARATION</span>;
    if (source.includes("Design Guidelines") && !source.includes("Declaration")) return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">GUIDELINE</span>;
    return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{source.split(" ")[0]}</span>;
  };

  const TABS = [
    { id: "submissions", label: "Submission Package", count: `${completeSections}/${totalSections}` },
    { id: "compliance", label: "Compliance", count: `${complianceCompleted}/${COMPLIANCE_RULES.length}` },
    { id: "timeline", label: "Timeline" },
    { id: "audit", label: "Audit", count: `${auditLog.length}` },
  ];

  const renderSection = (section, isOptional = false) => {
    const status = getSectionStatus(section);
    const isExpanded = expandedSections[section.id];
    const files = uploadedFiles[section.id] || [];
    const enabled = !isOptional || enabledOptional[section.id];

    if (isOptional && !enabled) {
      return (
        <Card key={section.id} className="opacity-50" data-testid={`section-${section.id}`}>
          <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => { setEnabledOptional(prev => ({ ...prev, [section.id]: true })); addAudit("status", "Section Enabled", section.title); setExpandedSections(prev => ({ ...prev, [section.id]: true })); }}>
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">{section.icon}</div>
            <div className="flex-1">
              <p className="font-semibold text-sm">{section.title}</p>
              <p className="text-xs text-muted-foreground">{section.conditional}</p>
            </div>
            <Button variant="outline" size="sm" className="h-7 text-xs">Add</Button>
          </div>
        </Card>
      );
    }

    return (
      <Card key={section.id} className={isExpanded ? "border-primary/30" : ""} data-testid={`section-${section.id}`}>
        <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setExpandedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))}>
          {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{section.icon}</div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">{section.title}</p>
            <p className="text-xs text-muted-foreground truncate">{section.section}</p>
          </div>
          {statusBadge(status)}
          {isOptional && (
            <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={(e) => { e.stopPropagation(); setEnabledOptional(prev => ({ ...prev, [section.id]: false })); addAudit("status", "Section Disabled", section.title); }}>
              <X className="w-3 h-3" />
            </Button>
          )}
        </div>

        {isExpanded && (
          <div className="px-4 pb-4 border-t space-y-4">
            <p className="text-xs text-muted-foreground italic pt-3 leading-relaxed">{section.description}</p>

            {/* Form Fields */}
            {section.type === "form" && section.fields && (
              <div className="grid gap-3">
                {section.fields.map(field => (
                  <div key={field.id}>
                    <Label className="text-xs">{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
                    {field.type === "textarea" ? (
                      <Textarea value={formData[field.id] || ""} onChange={e => { setFormData(prev => ({ ...prev, [field.id]: e.target.value })); }} placeholder={field.label} rows={3} className="text-sm mt-1" onBlur={() => addAudit("form", "Field Updated", field.label, section.title)} />
                    ) : (
                      <Input value={formData[field.id] || ""} onChange={e => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))} placeholder={field.label} className="text-sm mt-1" onBlur={() => addAudit("form", "Field Updated", field.label, section.title)} />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* File Upload Zone */}
            {(section.type === "upload" || section.type === "payment") && (
              <div>
                <div className="border-2 border-dashed rounded-lg p-5 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/20 transition-colors" onClick={() => fileInputRef.current[section.id]?.click()}>
                  <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-1" />
                  <p className="text-xs font-medium">Click to upload documents</p>
                  <p className="text-[10px] text-muted-foreground">PDF, images, DWG, DXF, ZIP</p>
                  <input ref={el => (fileInputRef.current[section.id] = el)} type="file" multiple className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.heic,.docx,.doc,.xlsx,.dwg,.dxf,.zip"
                    onChange={e => { if (e.target.files?.length) handleFileUpload(section.id, e.target.files); e.target.value = ""; }}
                  />
                </div>
                {files.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {files.map(f => (
                      <div key={f.id} className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md text-xs">
                        <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span className="flex-1 font-medium truncate">{f.name}</span>
                        <span className="text-muted-foreground">{f.size}</span>
                        <button onClick={() => handleRemoveFile(section.id, f.id)} className="text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Checklist */}
            {section.checklist && (
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Requirements Checklist</p>
                {section.checklist.map(item => {
                  const checked = checkedItems[item.id];
                  const linked = fileLinks[item.id];
                  const isLinking = linkingTarget === item.id;

                  return (
                    <div key={item.id} className={`p-2.5 rounded-md border transition-colors ${checked ? "bg-green-50 border-green-200" : "bg-card border-border"}`}>
                      <div className="flex items-start gap-2.5">
                        <div onClick={() => handleCheck(item.id, item.label, section.title)} className={`w-[18px] h-[18px] rounded flex-shrink-0 mt-0.5 border-2 flex items-center justify-center cursor-pointer transition-colors ${checked ? "border-green-600 bg-green-600 text-white" : "border-gray-300 bg-white"}`}>
                          {checked && <Check className="w-3 h-3" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs leading-relaxed ${checked ? "text-green-700 font-medium" : "text-foreground"}`}>
                            {item.label}
                            {!item.required && <span className="text-muted-foreground ml-1">(optional)</span>}
                          </p>
                          {item.source && (
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              {authorityBadge(item.source)}
                              <span className="text-[10px] text-muted-foreground italic">{item.source}</span>
                            </div>
                          )}
                          {linked && <div className="text-[10px] font-medium text-amber-600 mt-1 flex items-center gap-1"><Link2 className="w-3 h-3" />{linked.fileName}</div>}
                          {isLinking && files.length > 0 && (
                            <div className="mt-2 p-2 bg-white rounded border space-y-0.5">
                              <p className="text-[10px] font-bold text-muted-foreground">Select document:</p>
                              {files.map(f => (
                                <div key={f.id} onClick={() => handleLink(item.id, section.id, f.id)} className="p-1.5 rounded text-xs cursor-pointer hover:bg-muted/50"><FileText className="w-3 h-3 inline mr-1" />{f.name}</div>
                              ))}
                            </div>
                          )}
                        </div>
                        {section.type !== "certification" && section.type !== "form" && files.length > 0 && (
                          <button onClick={() => setLinkingTarget(isLinking ? null : item.id)} className={`text-[10px] font-bold px-2 py-1 rounded ${isLinking ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                            {linked ? "Relink" : "Link"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      <main className="flex-1 p-6" data-testid="drc-submission-page">
        <div className="max-w-4xl mx-auto">

          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold tracking-tight">DRC Submission Package</h1>
              <p className="text-sm text-muted-foreground">Horse Mountain Ranch Architectural Control Committee</p>
            </div>
            <Badge className={`h-7 ${submissionState === "ready" ? "bg-green-100 text-green-700" : submissionState === "submitted" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
              {submissionState.toUpperCase()}
            </Badge>
          </div>

          {/* Document Hierarchy Banner */}
          <Card className="mb-4 border-red-200 bg-red-50/50">
            <CardContent className="py-3 px-4">
              <div className="flex items-start gap-2">
                <Scale className="w-4 h-4 text-red-700 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-red-800 leading-relaxed">
                  <strong>Document Authority:</strong> Restated Declaration (1994, Book 661, Page 273) is the supreme governing instrument. Design Guidelines (2008) are supplementary and cannot supersede the Declaration. Where conflict exists, the Declaration controls absolutely.
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Progress Dashboard */}
          <Card className="mb-4">
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Overall Progress</span>
                <span className="text-sm font-bold">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2 mb-3" />
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-2 bg-muted/50 rounded">
                  <p className="text-lg font-bold">{completeSections}/{totalSections}</p>
                  <p className="text-[10px] text-muted-foreground">Sections</p>
                </div>
                <div className="p-2 bg-muted/50 rounded">
                  <p className="text-lg font-bold">{complianceCompleted}/{COMPLIANCE_RULES.length}</p>
                  <p className="text-[10px] text-muted-foreground">Compliance</p>
                </div>
                <div className="p-2 bg-muted/50 rounded">
                  <p className="text-lg font-bold">{Object.values(uploadedFiles).flat().length}</p>
                  <p className="text-[10px] text-muted-foreground">Documents</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <div className="flex gap-1 mb-4 bg-muted/50 rounded-lg p-1">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
                className={`flex-1 py-2 px-3 rounded-md text-xs font-semibold transition-colors ${activeTab === tab.id ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {tab.label} {tab.count && <span className="ml-1 text-[10px] opacity-70">({tab.count})</span>}
              </button>
            ))}
          </div>

          {/* ═══ TAB: SUBMISSIONS ═══ */}
          {activeTab === "submissions" && (
            <div className="space-y-3">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Required Submissions</p>
              {SUBMISSION_SECTIONS.filter(s => s.required).map(s => renderSection(s))}

              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mt-6">Conditional / Optional Submissions</p>
              {SUBMISSION_SECTIONS.filter(s => !s.required).map(s => renderSection(s, true))}

              {/* Missing Items */}
              {submissionState !== "ready" && submissionState !== "submitted" && activeSections.some(s => getSectionStatus(s) !== "complete") && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="py-3">
                    <p className="text-sm font-bold text-red-800 mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Submission Cannot Be Approved \u2014 Missing Items</p>
                    <div className="text-xs text-red-700 space-y-1">
                      {activeSections.filter(s => getSectionStatus(s) !== "complete").map(s => (
                        <div key={s.id}><X className="w-3 h-3 inline" /> <strong>{s.title}</strong> \u2014 {getSectionStatus(s) === "partial" ? "Incomplete" : "Not started"}</div>
                      ))}
                      {COMPLIANCE_RULES.filter(r => !complianceChecks[r.id]).length > 0 && (
                        <div><X className="w-3 h-3 inline" /> <strong>{COMPLIANCE_RULES.filter(r => !complianceChecks[r.id]).length} compliance rule(s)</strong> unconfirmed</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* ═══ TAB: COMPLIANCE ═══ */}
          {activeTab === "compliance" && (
            <Card>
              <CardContent className="py-4 space-y-4">
                <div>
                  <h2 className="text-lg font-bold">Compliance Validation</h2>
                  <p className="text-xs text-muted-foreground">All {COMPLIANCE_RULES.length} items must be confirmed. The ACC will independently verify.</p>
                </div>

                {/* Hierarchy Banner */}
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800 space-y-1.5">
                  <p className="font-bold flex items-center gap-1"><Scale className="w-4 h-4" /> Document Authority Hierarchy</p>
                  <div className="flex items-center gap-2"><span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-800">DECLARATION</span> Recorded covenant. Supreme authority.</div>
                  <div className="flex items-center gap-2"><span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">GUIDELINE</span> Supplementary. Subordinate to Declaration.</div>
                  <div className="flex items-center gap-2"><span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">CONFLICT</span> Declaration provision controls absolutely.</div>
                </div>

                {complianceCategories.map(cat => (
                  <div key={cat}>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5 pb-1 border-b">
                      {cat} ({COMPLIANCE_RULES.filter(r => r.category === cat && complianceChecks[r.id]).length}/{COMPLIANCE_RULES.filter(r => r.category === cat).length})
                    </p>
                    <div className="space-y-1">
                      {COMPLIANCE_RULES.filter(r => r.category === cat).map(rule => {
                        const checked = complianceChecks[rule.id];
                        const isConflict = rule.authority === "CONFLICT";
                        const auth = AUTHORITY[rule.authority] || AUTHORITY.GUIDELINE;
                        return (
                          <div key={rule.id} className={`flex items-start gap-2.5 p-2.5 rounded-md border ${isConflict ? "bg-red-50 border-red-200" : checked ? "bg-green-50 border-green-200" : "bg-card border-border"}`}>
                            <div onClick={() => { setComplianceChecks(prev => ({ ...prev, [rule.id]: !prev[rule.id] })); addAudit("compliance", !complianceChecks[rule.id] ? "Confirmed" : "Removed", rule.label); }}
                              className={`w-[18px] h-[18px] rounded flex-shrink-0 mt-0.5 border-2 flex items-center justify-center cursor-pointer ${checked ? "border-green-600 bg-green-600 text-white" : "border-gray-300 bg-white"}`}>
                              {checked && <Check className="w-3 h-3" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs ${isConflict ? "text-red-600 font-bold" : checked ? "text-green-700 font-medium" : ""}`}>{rule.label}</p>
                              {rule.note && <p className="text-[10px] text-muted-foreground italic mt-0.5">{isConflict ? "\u26A0 " : ""}{rule.note}</p>}
                            </div>
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${auth.bg} ${auth.color}`}>{auth.badge}</span>
                              <span className="text-[9px] font-mono text-muted-foreground">{rule.section.length > 28 ? rule.section.slice(0, 28) + "\u2026" : rule.section}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* ═══ TAB: TIMELINE ═══ */}
          {activeTab === "timeline" && (
            <Card>
              <CardContent className="py-4 space-y-5">
                <div>
                  <h2 className="text-lg font-bold">Review & Construction Timeline</h2>
                  <p className="text-xs text-muted-foreground">Per Declaration \u00A77.06, \u00A77.08, \u00A77.09</p>
                </div>

                {/* Review Stage Tracker */}
                <div className="flex gap-1 overflow-x-auto">
                  {REVIEW_STAGES.map((stage, idx) => (
                    <div key={stage.id} className={`flex-1 p-2.5 rounded-lg text-center min-w-[90px] transition-colors ${idx <= reviewStage ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                      <p className="text-[11px] font-bold">{stage.label}</p>
                      <p className="text-[9px] opacity-70">{stage.desc}</p>
                    </div>
                  ))}
                </div>

                {/* Timeline */}
                <div className="relative pl-8">
                  <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-border" />
                  {TIMELINE_EVENTS.map((event, idx) => (
                    <div key={event.id} className={`relative ${idx < TIMELINE_EVENTS.length - 1 ? "mb-5" : ""}`}>
                      <div className={`absolute -left-[22px] top-1 w-3 h-3 rounded-full border-[3px] border-background ${idx === 0 ? "bg-amber-500" : "bg-border"}`} />
                      <p className="text-sm font-bold">{event.label}</p>
                      <p className="text-xs text-muted-foreground">{event.desc}</p>
                      <p className="text-[10px] font-mono text-amber-600 mt-0.5">{event.days === 0 ? "Day 0" : `+${event.days} days`}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ═══ TAB: AUDIT ═══ */}
          {activeTab === "audit" && (
            <Card>
              <CardContent className="py-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold">Audit Trail</h2>
                    <p className="text-xs text-muted-foreground">Complete, timestamped record for ACC review</p>
                  </div>
                  <Badge variant="outline" className="font-mono">{auditLog.length} entries</Badge>
                </div>

                <div className="flex gap-1 flex-wrap">
                  {[{ id: "all", label: "All" }, { id: "upload", label: "Uploads" }, { id: "verify", label: "Verified" }, { id: "link", label: "Links" }, { id: "compliance", label: "Compliance" }, { id: "form", label: "Form" }, { id: "submit", label: "Submissions" }].map(f => (
                    <button key={f.id} onClick={() => setFilterAudit(f.id)} className={`px-3 py-1 rounded text-[11px] font-semibold transition-colors ${filterAudit === f.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{f.label}</button>
                  ))}
                </div>

                {filteredAudit.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground text-sm">{auditLog.length === 0 ? "No actions recorded yet." : "No matching entries."}</p>
                ) : (
                  <div className="space-y-0.5 max-h-[400px] overflow-y-auto">
                    {filteredAudit.map(entry => (
                      <div key={entry.id} className={`flex items-start gap-2.5 px-3 py-2 rounded text-xs ${entry.type === "submit" ? "bg-green-50" : entry.type === "remove" ? "bg-red-50" : "bg-muted/30"}`}>
                        <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${entry.type === "upload" ? "bg-blue-500" : entry.type === "verify" ? "bg-green-500" : entry.type === "link" ? "bg-amber-500" : entry.type === "remove" ? "bg-red-500" : entry.type === "compliance" ? "bg-purple-500" : entry.type === "submit" ? "bg-green-600" : "bg-gray-400"}`} />
                        <div className="flex-1">
                          <span className="font-bold">{entry.action}</span>
                          <span className="text-muted-foreground ml-1.5">{entry.detail}</span>
                          {entry.section && <span className="text-muted-foreground ml-1">({entry.section})</span>}
                        </div>
                        <span className="text-[10px] text-muted-foreground font-mono whitespace-nowrap">{entry.timestamp}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ═══ ACTIONS ═══ */}
          {submissionState !== "submitted" && (
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={handleSaveDraft} data-testid="save-draft-btn"><Save className="w-4 h-4 mr-1" /> Save Draft</Button>
              <Button onClick={() => isReady && setShowConfirmModal(true)} disabled={!isReady || saving} data-testid="submit-to-acc-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Submit to ACC for Review
              </Button>
            </div>
          )}

          {submissionState === "submitted" && (
            <Card className="mt-4 border-green-200 bg-green-50 text-center">
              <CardContent className="py-6">
                <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-2" />
                <h3 className="text-lg font-bold text-green-800">Submission Package Received</h3>
                <p className="text-sm text-green-700 mt-1">The ACC has 30 days to act (Declaration \u00A77.06). All {auditLog.length} audit entries preserved.</p>
              </CardContent>
            </Card>
          )}

          {/* Confirmation Modal */}
          <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm Submission</DialogTitle>
                <DialogDescription>Submit your complete DRC package to the Architectural Control Committee.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Per Declaration \u00A77.06, the Committee has <strong>30 days</strong> to act. No exterior construction, alteration, staking, clearing, excavation, or grading may begin until written approval is issued.
                </p>
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs font-bold text-red-800 leading-relaxed flex items-start gap-1">
                    <Scale className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    All ACC review decisions are governed by the Restated Declaration (Book 661, Page 273). The 2008 Design Guidelines are supplementary and shall not be applied where they conflict. Amendment requires 67% of Tract Owners per \u00A712.04.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="confirm-submit-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Confirm & Submit
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
}
